export const ALL_PERMISSION_KEYS = [
  'dashboard',
  'settings',
  'products',
  'inventory',
  'movements',
  'reports',
  'organizationUsers',
  'invitations',
  'warehouses',
  'categories',
  'units',
  'packagingTypes',
  'movementTypes',
  'customerTypes',
  'providers',
  'clients',
  'exchangeRates',
] as const;

export type AccessLevel = 'none' | 'read' | 'write' | 'admin';
export type PermissionKey = (typeof ALL_PERMISSION_KEYS)[number];
export type PermissionMap = Record<PermissionKey, AccessLevel>;
export type PlatformRole = 'None' | 'SuperAdmin';
export type OrganizationRoleName = 'Admin' | 'User' | 'ReadOnly';

export type PermissionRequirement = {
  key: PermissionKey;
  level: AccessLevel;
};

export type CurrentAppUserDto = {
  id: number;
  externalObjectId: string;
  email: string;
  displayName: string;
  platformRole: PlatformRole;
  organizationId: number | null;
  organizationName: string | null;
  organizationRole: OrganizationRoleName | null;
  isSuspended: boolean;
  permissions: PermissionMap;
  createdAt: string;
  updatedAt: string;
};

const ACCESS_LEVEL_ORDER: Record<AccessLevel, number> = {
  none: 0,
  read: 1,
  write: 2,
  admin: 3,
};

export const EMPTY_PERMISSION_MAP: PermissionMap = Object.fromEntries(
  ALL_PERMISSION_KEYS.map((key) => [key, 'none']),
) as PermissionMap;

export function normalizeAccessLevel(value: string | null | undefined): AccessLevel {
  switch (value) {
    case 'read':
    case 'write':
    case 'admin':
      return value;
    default:
      return 'none';
  }
}

export function normalizePermissionMap(
  value: Partial<Record<PermissionKey, string>> | null | undefined,
): PermissionMap {
  return ALL_PERMISSION_KEYS.reduce<PermissionMap>((result, key) => {
    result[key] = normalizeAccessLevel(value?.[key]);
    return result;
  }, { ...EMPTY_PERMISSION_MAP });
}

export function hasRequiredAccessLevel(
  currentLevel: AccessLevel | null | undefined,
  requiredLevel: AccessLevel,
): boolean {
  return ACCESS_LEVEL_ORDER[currentLevel ?? 'none'] >= ACCESS_LEVEL_ORDER[requiredLevel];
}
